
import { Canvas, Polygon, Text, Group } from "fabric";
import { pixelsToMillimeters } from "@/utils/measurement-utils";
import { createMeasurementLabelGroup, updateMeasurementText } from "./measurement-label-utils";
import { createMeasuredGroup } from "./group-utils";
import { ShapeType } from "@/types/fabric";

export type PolygonPoint = { x: number; y: number };

export const updatePolygonMeasurements = (polygon: Polygon, labels: (Text | Group)[], canvas: Canvas) => {
  if (!polygon) return;

  const parent = polygon.group;
  const parentScaleX = parent ? (parent.scaleX || 1) : 1;
  const parentScaleY = parent ? (parent.scaleY || 1) : 1;
  
  const points = polygon.points || [];
  
  for (let i = 0; i < points.length; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % points.length];
    
    const x1 = p1.x * parentScaleX;
    const y1 = p1.y * parentScaleY;
    const x2 = p2.x * parentScaleX;
    const y2 = p2.y * parentScaleY;
    
    const dx = x2 - x1;
    const dy = y2 - y1;
    const length = Math.sqrt(dx * dx + dy * dy);
    const lengthMm = Math.round(pixelsToMillimeters(length, canvas));
    
    if (labels[i]) {
      // Update with consistent styling
      updateMeasurementText(labels[i], `${lengthMm}mm`);
      
      // Update label angle if needed to align with the edge
      if (labels[i] instanceof Group) {
        const angle = Math.atan2(dy, dx) * (180 / Math.PI);
        labels[i].set({ angle: angle });
      }
    }
  }
  
  canvas.renderAll();
};

export const updateRhomboidMeasurements = (polygon: Polygon, labels: (Text | Group)[], canvas: Canvas) => {
  if (!polygon || labels.length !== 2) return;
  
  const parent = polygon.group;
  const parentScaleX = parent ? (parent.scaleX || 1) : 1;
  const parentScaleY = parent ? (parent.scaleY || 1) : 1;
  
  const points = polygon.points || [];
  if (points.length !== 4) return;
  
  // Extract the width label (top edge) and height label (left edge)
  const [widthLabel, heightLabel] = labels;
  
  // Calculate the width (horizontal dimension) - using the top edge (points 0 to 1)
  const topP1 = points[0];
  const topP2 = points[1];
  const widthDx = (topP2.x - topP1.x) * parentScaleX;
  const widthDy = (topP2.y - topP1.y) * parentScaleY;
  const width = Math.sqrt(widthDx * widthDx + widthDy * widthDy);
  const widthMm = Math.round(pixelsToMillimeters(width, canvas));
  
  // Calculate the height (vertical dimension) - using the left edge (points 3 to 0)
  const leftP1 = points[3];
  const leftP2 = points[0];
  const heightDx = (leftP2.x - leftP1.x) * parentScaleX;
  const heightDy = (leftP2.y - leftP1.y) * parentScaleY;
  const height = Math.sqrt(heightDx * heightDx + heightDy * heightDy);
  const heightMm = Math.round(pixelsToMillimeters(height, canvas));
  
  // Update the width label text
  updateMeasurementText(widthLabel, `${widthMm}mm`);
  
  // Update the height label text
  updateMeasurementText(heightLabel, `${heightMm}mm`);
  
  // Recalculate and update label positions relative to the shape
  
  // For width label - top edge
  const topAngle = Math.atan2(widthDy, widthDx) * (180 / Math.PI);
  widthLabel.set({ angle: topAngle });
  
  // For height label - left edge
  const leftAngle = Math.atan2(heightDy, heightDx) * (180 / Math.PI);
  heightLabel.set({ angle: leftAngle });
  
  canvas.renderAll();
};

export const createMeasuredRhomboid = (
  canvas: Canvas, 
  points: PolygonPoint[], 
  props: any
) => {
  const polygon = new Polygon(points, {
    ...props,
    objectCaching: false,
  });
  
  // Get current zoom for scaling
  const zoom = canvas.getZoom();
  
  const labels: Group[] = [];
  
  // Calculate the horizontal and vertical dimensions
  // For rhomboid we need width (along the top) and height (vertical)
  
  // Find the width (horizontal dimension) - using the top edge (points 0 to 1)
  const topP1 = points[0];
  const topP2 = points[1];
  const widthDx = topP2.x - topP1.x;
  const widthDy = topP2.y - topP1.y;
  const width = Math.sqrt(widthDx * widthDx + widthDy * widthDy);
  const widthMm = Math.round(pixelsToMillimeters(width, canvas));
  
  // Calculate midpoint and angle of the top edge
  const topMidX = (topP1.x + topP2.x) / 2;
  const topMidY = (topP1.y + topP2.y) / 2;
  const topAngle = Math.atan2(widthDy, widthDx) * (180 / Math.PI);
  
  // Create label for width (place it above the top edge)
  const perpAngleTop = (topAngle - 90) * (Math.PI / 180); // -90 to place it above
  const offset = 12; // Consistent offset
  const topOffsetX = Math.cos(perpAngleTop) * offset;
  const topOffsetY = Math.sin(perpAngleTop) * offset;
  
  const widthLabel = createMeasurementLabelGroup(
    `${widthMm}mm`,
    props.left + topMidX + topOffsetX,
    props.top + topMidY + topOffsetY,
    topAngle,
    true,
    zoom
  );
  
  labels.push(widthLabel);
  
  // Find the height (vertical dimension) - using the left edge (points 3 to 0)
  const leftP1 = points[3];
  const leftP2 = points[0];
  const heightDx = leftP2.x - leftP1.x;
  const heightDy = leftP2.y - leftP1.y;
  const height = Math.sqrt(heightDx * heightDx + heightDy * heightDy);
  const heightMm = Math.round(pixelsToMillimeters(height, canvas));
  
  // Calculate midpoint and angle of the left edge
  const leftMidX = (leftP1.x + leftP2.x) / 2;
  const leftMidY = (leftP1.y + leftP2.y) / 2;
  const leftAngle = Math.atan2(heightDy, heightDx) * (180 / Math.PI);
  
  // Create label for height (place it to the left of the left edge)
  const perpAngleLeft = (leftAngle - 90) * (Math.PI / 180); // -90 to place it to the left
  const leftOffsetX = Math.cos(perpAngleLeft) * offset;
  const leftOffsetY = Math.sin(perpAngleLeft) * offset;
  
  const heightLabel = createMeasurementLabelGroup(
    `${heightMm}mm`,
    props.left + leftMidX + leftOffsetX,
    props.top + leftMidY + leftOffsetY,
    leftAngle,
    true,
    zoom
  );
  
  labels.push(heightLabel);
  
  const group = createMeasuredGroup(canvas, polygon, labels, 'rhomboid');
  
  return group;
};

export const createMeasuredPolygon = (
  canvas: Canvas, 
  points: PolygonPoint[], 
  props: any,
  shapeType: ShapeType = 'polygon'
) => {
  const polygon = new Polygon(points, {
    ...props,
    objectCaching: false,
  });
  
  // Get current zoom for scaling
  const zoom = canvas.getZoom();
  
  const labels: Group[] = [];
  
  for (let i = 0; i < points.length; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % points.length];
    
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    const lengthMm = Math.round(pixelsToMillimeters(length, canvas));
    
    // Calculate midpoint of this edge
    const midX = (p1.x + p2.x) / 2;
    const midY = (p1.y + p2.y) / 2;
    
    // Calculate angle of this edge
    const angle = Math.atan2(dy, dx) * (180 / Math.PI);
    
    // Calculate perpendicular offset for consistent placement
    const perpAngle = (angle + 90) * (Math.PI / 180);
    const offset = 12; // Consistent offset
    const offsetX = Math.cos(perpAngle) * offset;
    const offsetY = Math.sin(perpAngle) * offset;
    
    // Use our standardized label creation with proper styling and zoom handling
    const label = createMeasurementLabelGroup(
      `${lengthMm}mm`,
      props.left + midX + offsetX,
      props.top + midY + offsetY,
      angle,
      true,
      zoom
    );
    
    labels.push(label);
  }

  const group = createMeasuredGroup(canvas, polygon, labels, shapeType);
  
  return group;
};
